import type { ReactNode } from "react";
import type { DataModel, MainTab, PlanConfig, PreviewTab, ReportConfig, ReportConfigurationLookupOption } from "../types";
import type { RuleBook } from "../logic/narrative";
import { RuleBookPreview } from "./RuleBookPreview";
import { RuleSummaryPreview } from "./RuleSummaryPreview";

// TEMPORARY: the Rule Book preview tab is hidden. Flip to true to bring it back - the
// view itself (RuleBookPreview / logic/narrative.ts) is untouched. The page's default tab
// follows this flag too (see ReportConfiguration.tsx).
export const SHOW_RULE_BOOK_TAB = false;

const SQL_KEYWORDS = new Set([
    "AND",
    "APPLY",
    "AS",
    "ASC",
    "BETWEEN",
    "BY",
    "CASE",
    "CAST",
    "COUNT",
    "CROSS",
    "DATEADD",
    "DATEDIFF",
    "DATEFROMPARTS",
    "DAY",
    "DECIMAL",
    "DESC",
    "DISTINCT",
    "ELSE",
    "END",
    "ESCAPE",
    "FROM",
    "GETDATE",
    "GROUP",
    "IN",
    "IS",
    "LIKE",
    "MONTH",
    "NOT",
    "NULL",
    "NULLIF",
    "NVARCHAR",
    "OR",
    "ORDER",
    "OVER",
    "SELECT",
    "SUM",
    "THEN",
    "WHEN",
    "WHERE",
    "WITH",
]);

const SQL_FUNCTIONS = new Set([
    "ABS",
    "CAST",
    "CONCAT",
    "COUNT",
    "DATEADD",
    "DATEDIFF",
    "DATEFROMPARTS",
    "GETDATE",
    "LTRIM",
    "NULLIF",
    "RTRIM",
    "STUFF",
    "SUM",
]);

interface PreviewPanelProps {
    downloadQueue?: ReactNode;
    hasPlans: boolean;
    previewOpen: boolean;
    mainTab: MainTab;
    plan: PlanConfig;
    report?: ReportConfig;
    dataModels: Record<string, DataModel>;
    // Passed straight through to RuleSummaryPreview, which uses it to name a metric's
    // Co-Plan clients.
    clientOptions?: ReportConfigurationLookupOption[];
    // The narrative view of this report, already built (logic/narrative.ts).
    ruleBook: RuleBook | null;
    planRulePreview: string;
    previewTab: PreviewTab;
    previewText: string;
    onClose: () => void;
    onOpen: () => void;
    onPreviewTabChange: (tab: PreviewTab) => void;
}

export function PreviewPanel({
    hasPlans,
    previewOpen,
    mainTab,
    plan,
    report,
    dataModels,
    clientOptions,
    ruleBook,
    planRulePreview,
    previewTab,
    previewText,
    onClose,
    onOpen,
    onPreviewTabChange,
    downloadQueue,
}: PreviewPanelProps) {
    if (!hasPlans) return null;

    if (!previewOpen) {
        return (
            <aside className="rc-preview-collapsed">
                <div className="rc-download-actions">
                    {downloadQueue}
                <button className="rc-preview-toggle" type="button" onClick={onOpen}>
                    <span className="material-icons md-16" aria-hidden="true">visibility</span>
                    <span>Preview</span>
                </button>
                </div>
            </aside>
        );
    }

    return (
        <>
            <button className="rc-preview-backdrop" type="button" aria-label="Close Rule Preview" onClick={onClose} />
            <aside className="rc-preview" aria-label="Rule Preview">
                <div className="rc-preview-heading-row">
                    <h4><span><span className="material-icons md-16" aria-hidden="true">visibility</span> Rule Preview</span></h4>
                    {downloadQueue}
                    <button className="rc-iconbtn" type="button" aria-label="Close Rule Preview" title="Close Preview" onClick={onClose}>
                        <span className="material-icons md-16" aria-hidden="true">close</span>
                    </button>
                </div>
            {mainTab === "plan" ? (
                <pre className="rc-preview-text">{planRulePreview}</pre>
            ) : (
                <>
                    <div className="rc-preview-toolbar">
                        <div className="rc-tabbar small">
                            {SHOW_RULE_BOOK_TAB && <button className={previewTab === "ruleBook" ? "rc-tab active" : "rc-tab"} onClick={() => onPreviewTabChange("ruleBook")} title="The report written out in plain language">Rule Book</button>}
                            <button className={previewTab === "summary" ? "rc-tab active" : "rc-tab"} onClick={() => onPreviewTabChange("summary")}>Summary</button>
                            <button className={previewTab === "details" ? "rc-tab active" : "rc-tab"} onClick={() => onPreviewTabChange("details")}>Rule Details</button>
                            <button className={previewTab === "sql" ? "rc-tab active" : "rc-tab"} onClick={() => onPreviewTabChange("sql")}>SQL</button>
                            <button className={previewTab === "technicalSql" ? "rc-tab active" : "rc-tab"} onClick={() => onPreviewTabChange("technicalSql")} title="Same query, using ID columns instead of value comparisons where available">Metric SQL</button>
                            <button className={previewTab === "excludedSql" ? "rc-tab active" : "rc-tab"} onClick={() => onPreviewTabChange("excludedSql")} title="Queries behind Download Excluded Records: same reporting period and health plan, rows that fail any other metric condition">Excluded SQL</button>
                        </div>
                        {(previewTab === "sql" || previewTab === "technicalSql" || previewTab === "excludedSql" || previewTab === "ruleBook") && (
                            <button className="rc-iconbtn rc-copy" type="button" aria-label={previewTab === "ruleBook" ? "Copy Rule Book" : "Copy SQL"} title={previewTab === "ruleBook" ? "Copy Rule Book" : "Copy SQL"} onClick={() => navigator.clipboard?.writeText(previewText)}>
                                <span className="material-icons md-16" aria-hidden="true">content_copy</span>
                            </button>
                        )}
                    </div>
                    {previewTab === "sql" || previewTab === "technicalSql" || previewTab === "excludedSql"
                        ? <SqlPreviewBlock sql={previewText} title={previewTab === "excludedSql" ? "Excluded Records SQL" : undefined} />
                        : previewTab === "ruleBook"
                            ? <RuleBookPreview book={ruleBook} />
                            : <RuleSummaryPreview plan={plan} report={report} mode={previewTab} dataModels={dataModels} clientOptions={clientOptions} />}
                </>
            )}
            </aside>
        </>
    );
}

function SqlPreviewBlock({ sql, title }: { sql: string; title?: string }) {
    return (
        <div className="rc-sql-preview-card">
            <div className="rc-sql-preview-head">
                <span><span className="material-icons md-16" aria-hidden="true">data_object</span> {title || "Generated SQL"}</span>
                <span>Read-Only Preview</span>
            </div>
            <pre className="rc-preview-code rc-sql-code">{renderHighlightedSql(sql)}</pre>
        </div>
    );
}

function renderHighlightedSql(sql: string) {
    const lines = sql.split(/\r?\n/);
    return lines.map((line, lineIndex) => (
        <span className="rc-sql-line" key={lineIndex}>
            {highlightSqlLine(line, lineIndex)}
            {lineIndex < lines.length - 1 ? "\n" : null}
        </span>
    ));
}

function highlightSqlLine(line: string, lineIndex: number) {
    const commentIndex = line.indexOf("--");
    const code = commentIndex >= 0 ? line.slice(0, commentIndex) : line;
    const comment = commentIndex >= 0 ? line.slice(commentIndex) : "";
    return (
        <>
            {highlightSqlCode(code, `sql-${lineIndex}`)}
            {comment && <span className="rc-sql-comment">{comment}</span>}
        </>
    );
}

function highlightSqlCode(code: string, keyPrefix: string) {
    const parts: JSX.Element[] = [];
    const tokenRegex = /('[^']*(?:''[^']*)*'|\[[^\]]+\]|@\w+|\b\d+(?:\.\d+)?\b|\b[A-Za-z_][A-Za-z0-9_]*\b)/g;
    let lastIndex = 0;
    let match = tokenRegex.exec(code);
    let tokenIndex = 0;

    while (match !== null) {
        if (match.index > lastIndex) {
            parts.push(<span key={`${keyPrefix}-text-${tokenIndex}`}>{code.slice(lastIndex, match.index)}</span>);
            tokenIndex += 1;
        }

        const token = match[0];
        parts.push(<span className={sqlTokenClass(token, code, tokenRegex.lastIndex)} key={`${keyPrefix}-token-${tokenIndex}`}>{token}</span>);
        tokenIndex += 1;
        lastIndex = tokenRegex.lastIndex;
        match = tokenRegex.exec(code);
    }

    if (lastIndex < code.length) {
        parts.push(<span key={`${keyPrefix}-text-end`}>{code.slice(lastIndex)}</span>);
    }

    return parts;
}

function sqlTokenClass(token: string, code: string, endIndex: number) {
    if (token.startsWith("'")) return "rc-sql-string";
    if (token.startsWith("@")) return "rc-sql-param";
    if (token.startsWith("[")) return "rc-sql-identifier";
    if (/^\d/.test(token)) return "rc-sql-number";

    const upper = token.toUpperCase();
    const rest = code.slice(endIndex).trimStart();
    if (SQL_FUNCTIONS.has(upper) && rest.startsWith("(")) return "rc-sql-function";
    if (SQL_KEYWORDS.has(upper)) return "rc-sql-keyword";
    return "rc-sql-identifier";
}
